<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useProfileStore } from '@/stores/profile'
import { useTranslaterStore } from '@/stores/translater'
import { useUsersStore } from '@/stores/users'
import { useUsersGroupsStore } from '@/stores/users-groups'
import { useRouter } from 'vue-router'
import UTAUserGroup from '@/components/user/UTAUserGroup.vue'
import Modal from '@/components/UTAModal.vue'
import checkArrayIncludes from '@/helpers/checkArrayIncludes'

const props = defineProps({
    uuid: {
        type: String
    }
})

const router = useRouter()

const profileStore      = useProfileStore()
const translaterStore   = useTranslaterStore()
const usersStore        = useUsersStore()
const usersGroupsStore  = useUsersGroupsStore()

const profile           = computed(() => profileStore.profile)
const getTranslate      = computed(() => translaterStore.getTranslate)
const user              = computed(() => usersStore.user)
const userUpdateMsg     = computed(() => usersStore.userUpdateMsg)
const userDeleteProcess = computed(() => usersStore.userDeleteProcess)
const userDeleteMsg     = computed(() => usersStore.userDeleteMsg)
const userAttributes    = computed(() => usersStore.userAttributes)
const isProfilePage     = computed(() => profile.value.uuid == props.uuid)

const userUuid          = ref('')
const allowedUserAttrs  = ref([])
// const isSelfProfile     = ref(true)
const editUser          = ref(false)
const editPrivates      = ref(false)
const formInvalids      = {}
const formErrors        = ref([])
const showUserDelete    = ref(false)

const UTAUserInfoName           = ref('')
const UTAUserInfoSurname        = ref('')
const UTAUserInfoLastname       = ref('')
const UTAUserInfoEmail          = ref('')
const UTAUserInfoPhone          = ref('')
const UTAUserInfoAttr           = ref([])
const UTAUserInfoGroupUuid      = ref('')
const UTAUserInfoGroupName      = ref('')
const UTAUserInfoGroupUuidNew   = ref('')
const UTAUserInfoGroupNameNew   = ref('')
const UTAUserLock               = ref(false)

userUuid.value = props.uuid || localStorage.getItem('UTA_UUID')

function updateUserParams(user) {

    if(user) {

        UTAUserInfoName.value       = user.name
        UTAUserInfoSurname.value    = user.surname
        UTAUserInfoLastname.value   = user.lastName
        UTAUserInfoEmail.value      = user.email
        UTAUserInfoPhone.value      = user.phone
        UTAUserInfoAttr.value       = user.attr?.array || []
        UTAUserLock.value           = user.lock || false
        UTAUserInfoGroupUuid.value  = user.ugroup

        if(user.ugroup) {
            usersGroupsStore.readUsersGroups({ uuid_array: user.ugroup }).then(groups => {
                if(groups.array?.length) {
                    UTAUserInfoGroupName.value = UTAUserInfoGroupNameNew.value = groups.array[0].name
                    UTAUserInfoGroupUuidNew.value = groups.array[0].uuid
                }
            })
        } else {
            UTAUserInfoGroupName.value = UTAUserInfoGroupNameNew.value = getTranslate.value.USER_GROUP_EMPTY
        }
    }
}

function updateUserAttr(attr, toggle = true) {

    if(toggle) {

        UTAUserInfoAttr.value.includes(attr) ? 
            UTAUserInfoAttr.value = UTAUserInfoAttr.value.filter( item => item !== attr) : 
            UTAUserInfoAttr.value.push(attr)

    } else {

        if(!UTAUserInfoAttr.value.includes(attr)) 
            UTAUserInfoAttr.value.push(attr)
    }
}

function userGroupSelected(event) {

    if(!event) {

        UTAUserInfoGroupUuidNew.value = ''
        UTAUserInfoGroupNameNew.value = getTranslate.value.USER_GROUP_EMPTY
        return 
    }

    UTAUserInfoGroupUuidNew.value = event.groupUuid
    UTAUserInfoGroupNameNew.value = event.groupName
}

function applyUser(user) {
    if(user.uuid) {
        updateUserParams(user)

        if(profile.value.attr?.array.includes('SUPER_ADMIN')) {
            allowedUserAttrs.value = userAttributes.value
        } else {
            userAttributes.value.map(attr => {
                if(profile.value.attr?.array.includes(attr)) {
                    allowedUserAttrs.value.push(attr)
                }
            })
        }

        if(user.ugroup) {
            usersGroupsStore.readUsersGroups({ uuid_array: user.ugroup }).then(groups => {
                if(groups.array?.length) {
                    UTAUserInfoGroupName.value = UTAUserInfoGroupNameNew.value = groups.array[0].name
                    UTAUserInfoGroupUuidNew.value = groups.array[0].uuid
                }
            })
        } else {
            UTAUserInfoGroupName.value = UTAUserInfoGroupNameNew.value = getTranslate.value.USER_GROUP_EMPTY
        }

        // isSelfProfile.value = user.uuid == profile.value.uuid
    }
}

function submitUpdateUser(event) {

    editPrivates.value = checkArrayIncludes(profile.value.attr?.array, ["EDIT_PRIVATE_CAR", "SUPER_ADMIN"])

    formInvalids.value = {}
    formErrors.value = []

    const fields = event.target
    let payload = {
        uuid: userUuid.value
    }

    if(fields.UTAUserInfoPhone.value) {
        payload.phone = fields.UTAUserInfoPhone.value
    } else {
        formErrors.value.push(getTranslate.value.USER_PHONE)
        formInvalids.value.UTAUserInfoPhone = true
    }

    if(fields.UTAUserInfoEmail.value) {
        payload.email = fields.UTAUserInfoEmail.value
    } else {
        formErrors.value.push(getTranslate.value.USER_EMAIL)
        formInvalids.value.UTAUserInfoEmail = true
    }

    payload.name        = fields.UTAUserInfoName.value
    payload.surname     = fields.UTAUserInfoSurname.value
    payload.lastName    = fields.UTAUserInfoLastname.value
    payload.ugroup      = UTAUserInfoGroupUuidNew.value
    payload.lock        = UTAUserLock.value

    if(!isProfilePage.value) payload.attr = { array: UTAUserInfoAttr.value }

    if(formErrors.value.length == 0) {
        usersStore.updateUser(payload)
    }
}

onBeforeMount(() => {
    if(!isProfilePage.value) {
        applyUser(user.value)
    } else {
        applyUser(profile.value)
    }
})

watch(userUpdateMsg, msg => {
    if('success' in msg && msg.success) {
        updateUserParams(user.value)
        editUser.value = false
        if(msg.logout) {
            location.reload()
        }
    }
})

watch(userDeleteMsg, msg => {
    if(
        'success' in msg && 
        msg.success && 
        msg.data?.uuid == props.uuid
    ) {
        showUserDelete.value = false
        usersStore.readUsers({ 'c.limit': 10 })
        router.push('/users')
    }
})

</script>

<template>
    <form class="uta-user-info" @submit.prevent="submitUpdateUser($event)">
        <header class="uta-page-header" role="heading" aria-level="1">
            <h1 class="uta-page-header__title">{{ getTranslate.USER_INFO }}</h1>
            <div class="uta-page-header__actions">
                <template v-if="!editUser">
                    <button 
                        class="uta-page-header__action uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon" 
                        type="button"
                        @click.prevent="editUser = true" 
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons"> edit </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_EDIT }}</span>
                        </span>
                    </button>
                </template>
                <template v-else>
                    <button
                        type="button"
                        @click.prevent="editUser = false" 
                        class="uta-page-header__action uta-btn uta-btn--sm uta-btn--has-icon"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons"> cancel </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_CANCEL }}</span>
                        </span>
                    </button>
                    <button
                        type="submit"
                        class="uta-page-header__action uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons"> save </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_SAVE }}</span>
                        </span>
                    </button>
                </template>
            </div>
        </header>
        <div class="uta-alert uta-alert--warning my-4">
            <div class="uta-alert__icon">
                <span class="material-icons alert__icon">
                    warning
                </span>
            </div>
            <div class="uta-alert__text">
                {{ getTranslate.USER_ATTR_WARNING }}
            </div>
        </div>
        <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate.EFormFields }}:
                <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                    {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                </span>
            </div>
            <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div 
            v-if="'success' in userUpdateMsg"
            class="my-4 uta-alert" 
            :class="{
                'uta-alert--success'    : userUpdateMsg.success, 
                'uta-alert--danger'     : !userUpdateMsg.success
            }"
        >
            <div class="uta-alert__icon">
                <span class="material-icons">
                    {{ userUpdateMsg.success ? 'check_circle' : 'error_outline' }}
                </span>
            </div>
            <div class="uta-alert__text">
                {{ getTranslate[userUpdateMsg.text] || userUpdateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="usersStore.setUpdateUserMsg({})">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div class="uta-user-info__top">
            <div class="row uta-user-info__top-params">
                <div class="col-12 col-sm-6 col-xl-4">
                    <div class="uta-user-info__top-param">
                        <div class="uta-user-info__top-param-name d-flex align-items-center">
                            <div class="uta-user-info__top-param-name-text">
                                {{ getTranslate.USER_NAME }}
                            </div>
                        </div>
                        <div class="uta-user-info__top-param-value">
                            <template v-if="!editUser && !isProfilePage">
                                {{ user.name || '&mdash;' }}
                            </template>
                            <template v-if="!editUser && isProfilePage">
                                {{ profile.name || '&mdash;' }}
                            </template>
                            <template v-if="editUser">
                                <input
                                    class="form-control" 
                                    type="text" 
                                    id="UTAUserInfoName"
                                    :value="UTAUserInfoName"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-xl-4">
                    <div class="uta-user-info__top-param">
                        <div class="uta-user-info__top-param-name d-flex align-items-center">
                            <div class="uta-user-info__top-param-name-text">
                                {{ getTranslate.USER_SURNAME }}
                            </div>
                        </div>
                        <div class="uta-user-info__top-param-value">
                            <template v-if="!editUser && !isProfilePage">
                                {{ user.surname || '&mdash;' }}
                            </template>
                            <template v-if="!editUser && isProfilePage">
                                {{ profile.surname || '&mdash;' }}
                            </template>
                            <template v-if="editUser">
                                <input
                                    class="form-control" 
                                    type="text" 
                                    id="UTAUserInfoSurname"
                                    :value="UTAUserInfoSurname"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-xl-4">
                    <div class="uta-user-info__top-param">
                        <div class="uta-user-info__top-param-name d-flex align-items-center">
                            <div class="uta-user-info__top-param-name-text">
                                {{ getTranslate.USER_LASTNAME }}
                            </div>
                        </div>
                        <div class="uta-user-info__top-param-value">
                            <template v-if="!editUser && !isProfilePage">
                                {{ user.lastName || '&mdash;' }}
                            </template>
                            <template v-if="!editUser && isProfilePage">
                                {{ profile.lastName || '&mdash;' }}
                            </template>
                            <template v-if="editUser">
                                <input
                                    class="form-control" 
                                    type="text" 
                                    id="UTAUserInfoLastname"
                                    :value="UTAUserInfoLastname"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-xl-4">
                    <div class="uta-user-info__top-param">
                        <div class="uta-user-info__top-param-name d-flex align-items-center">
                            <div class="uta-user-info__top-param-name-text">
                                {{ getTranslate.USER_EMAIL }}
                            </div>
                        </div>
                        <div class="uta-user-info__top-param-value">
                            <template v-if="!editUser && !isProfilePage">
                                {{ user.email || '&mdash;' }}
                            </template>
                            <template v-if="!editUser && isProfilePage">
                                {{ profile.email || '&mdash;' }}
                            </template>
                            <template v-if="editUser">
                                <input
                                    class="form-control" 
                                    type="text" 
                                    id="UTAUserInfoEmail"
                                    :value="UTAUserInfoEmail"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-xl-4">
                    <div class="uta-user-info__top-param">
                        <div class="uta-user-info__top-param-name d-flex align-items-center">
                            <div class="uta-user-info__top-param-name-text">
                                {{ getTranslate.USER_PHONE }}
                            </div>
                        </div>
                        <div class="uta-user-info__top-param-value">
                            <template v-if="!editUser && !isProfilePage">
                                {{ user.phone || '&mdash;' }}
                            </template>
                            <template v-if="!editUser && isProfilePage">
                                {{ profile.phone || '&mdash;' }}
                            </template>
                            <template v-if="editUser">
                                <input
                                    class="form-control" 
                                    type="text" 
                                    id="UTAUserInfoPhone"
                                    :value="UTAUserInfoPhone"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-xl-4">
                    <div class="uta-user-info__top-param">
                        <div class="uta-user-info__top-param-name d-flex align-items-center">
                            <div class="uta-user-info__top-param-name-text">
                                {{ getTranslate.USER_GROUP }}
                            </div>
                        </div>
                        <div class="uta-user-info__top-param-value">
                            <template v-if="editUser">
                                <span class="material-icons uta-user-info__top-param-value-btn" v-if="(UTAUserInfoGroupUuid || UTAUserInfoGroupUuidNew) && editUser" @click="userGroupSelected(false)">close</span>
                                {{ UTAUserInfoGroupNameNew || getTranslate.USER_GROUP_EMPTY }}
                                <UTAUserGroup @userGroupSelected="userGroupSelected" v-if="editUser" />
                            </template>
                            <template v-else>
                                <template v-if="UTAUserInfoGroupUuid || UTAUserInfoGroupUuidNew">
                                    <router-link :to="'/users-groups/' + (UTAUserInfoGroupUuidNew || UTAUserInfoGroupUuid)">
                                        {{ UTAUserInfoGroupName }}
                                    </router-link>
                                </template>
                                <template v-else>
                                    {{ UTAUserInfoGroupName }}
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div 
            class="mt-5 mb-3 uta-user-info__switchers" 
            v-if="allowedUserAttrs.length && !isProfilePage"
        >

            <h3 class="my-4">{{ getTranslate.ATTRS }}</h3>

            <div class="d-flex flex-wrap">
                <template v-if="!editUser">
                    <div 
                        class="switcher mb-3 uta-user-info__switchers-switcher" 
                        :class="{ 'uta-user-info__switchers-switcher--selected' : UTAUserInfoAttr.includes(attr.toUpperCase()) }"
                        v-for="attr in allowedUserAttrs" 
                        :key="attr"
                    >
                        {{ getTranslate['ATTR_'  + attr.toUpperCase() ] || attr }}
                        <span class="material-icons uta-user-info__switchers-switcher-icon" v-if="UTAUserInfoAttr.includes(attr.toUpperCase())">done</span>
                    </div>
                </template>
                <template v-else>
                    <div 
                        class="switcher mb-3 uta-user-info__switchers-switcher" 
                        v-for="attr in allowedUserAttrs" 
                        :key="attr"
                    >
                        <input 
                            class="switcher__input" 
                            type="checkbox" 
                            :id="'UserAttr' + attr" 
                            :checked="UTAUserInfoAttr.includes(attr.toUpperCase())" 
                            @change="updateUserAttr(attr, true)"
                        >
                        <label 
                            class="switcher__label" 
                            :for="'UserAttr' + attr"
                        >
                            <span class="switcher__text">{{ getTranslate['ATTR_'  + attr.toUpperCase() ] || attr }}</span>
                        </label>
                    </div>
                </template>
            </div>

            <hr>

            <div class="mt-5 row">
                <div class="col-12 col-md-6">
                    <div 
                        class="mb-3 uta-user-info__lock" 
                        v-if="user.attr && !isProfilePage"
                    >
                        <div class="d-flex flex-wrap">
                            <div 
                                class="switcher uta-user-info__lock-switcher" 
                                :class="{ 'uta-user-info__lock-switcher--selected' : UTAUserLock }"
                            >
                                <template v-if="!editUser">
                                    <span 
                                        class="material-icons me-2" 
                                        v-if="UTAUserLock"
                                    >
                                        dangerous
                                    </span>
                                    {{ !UTAUserLock ? getTranslate.USER_STATUS_ACTIVE : getTranslate.USER_STATUS_LOCKED }}
                                </template>
                                <template v-else>
                                    <input 
                                        class="switcher__input" 
                                        type="checkbox" 
                                        id="UTAUserLock" 
                                        :checked="UTAUserLock" 
                                        @change="UTAUserLock = $event.target.checked"
                                    >
                                    <label 
                                        class="switcher__label" 
                                        for="UTAUserLock"
                                    >
                                        <span class="switcher__text">{{ getTranslate.USER_STATUS_LOCK }}</span>
                                    </label>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 text-md-end">
                    <div 
                        class="uta-user-info__updated" 
                        v-if="user.status"
                    >
                        {{ getTranslate.USER_UPDATED }}: {{ new Date(user.status.updatedAt * 1000).toLocaleString() }}
                    </div>
                </div>
            </div>

            <div class="uta-btns">
                <button type="button" class="uta-btn" @click="showUserDelete = true">
                    {{ getTranslate.BTN_DELETE }}
                </button>
            </div>

        </div>

        <transition name="bounce">
            <Modal 
                v-if="showUserDelete"
                :header="getTranslate.USERS_DELETE"
                size="md"
                @closeModal="showUserDelete = false"
            >
                <template v-slot:body>
                    <div
                        v-if="'success' in userDeleteMsg && !userDeleteMsg.success" 
                        class="mb-4 uta-alert uts-alert--danger"
                    >
                        {{ getTranslate[userDeleteMsg.text] || userDeleteMsg.text }}
                    </div>
                    {{ getTranslate.USERS_DELETE_DESCR }}
                    <div class="mt-4 uta-btns">
                        <button 
                            type="button" 
                            class="uta-btn"
                            @click="showUserDelete = false"
                        >
                            {{ getTranslate.BTN_CANCEL }}
                        </button>
                        <button
                            type="button" 
                            class="uta-btn uta-btn--main"
                            @click.prevent="usersStore.deleteUser(props.uuid)"
                        >
                            <template v-if="!userDeleteProcess">
                                {{ getTranslate.BTN_DELETE }}
                            </template>
                            <template v-else>
                                <div class="spinner-border" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </template>
                        </button>
                    </div>
                </template>
            </Modal>
        </transition>

    </form>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/user/user-info';
</style>